// Enums
import { DATA_PROVIDER_NAME } from '@/enums/DataProvider';
import { InstrumentAssetStatus } from '@/enums/Instrument';

// Types
import Instrument from '@/types/instruments/instrument';
import InstrumentDisclaimer from '@/types/instrument-disclaimer';
import LocalizedString from '@/types/localized-string';
import ShelvableInstrument from '@/types/shelvable-instrument';
import { AltPerformance } from '@/types/instruments/instrument-performance';

export enum AltClass {
  CTA = 'CTA',
  HEDGE_FUNDS = 'Hedge Funds',
  INFRASTRUCTURE = 'Infrastructure',
  MULTI = 'Multi',
  NATURAL_RESOURCES = 'Natural Resources',
  PRIVATE_DEBT = 'Private Debt',
  PRIVATE_EQUITY = 'Private Equity',
  REAL_ESTATE = 'Real Estate',
  VENTURE_CAPITAL = 'Venture Capital',
  // French translations
  CAPITAL_INVESTISSEMENT = 'Capital-investissement',
  CAPITAL_RISQUE = 'Capital-risque',
  DETTE_PRIVEE = 'Dette privée',
  FONDS_SPECULATIFS = 'Fonds spéculatifs',
  IMMOBILIER = 'Immobilier',
  RESSOURCES_NATURELLES = 'Ressources naturelles',
}

export enum AltClassTruncated {
  CTA = 'CTA',
  HEDGE_FUNDS = 'HF',
  INFRASTRUCTURE = 'INF',
  MULTI = 'MULTI',
  NATURAL_RESOURCES = 'NR',
  PRIVATE_DEBT = 'PD',
  PRIVATE_EQUITY = 'PE',
  REAL_ESTATE = 'RE',
  VENTURE_CAPITAL = 'VC',
  // French translations
  CAPITAL_INVESTISSEMENT = 'CI',
  CAPITAL_RISQUE = 'CR',
  DETTE_PRIVEE = 'DP',
  FONDS_SPECULATIFS = 'FS',
  IMMOBILIER = 'IM',
  RESSOURCES_NATURELLES = 'RN',
}

export const AltClassesTruncated: Record<AltClass, AltClassTruncated> = {
  [AltClass.CTA]: AltClassTruncated.CTA,
  [AltClass.HEDGE_FUNDS]: AltClassTruncated.HEDGE_FUNDS,
  [AltClass.INFRASTRUCTURE]: AltClassTruncated.INFRASTRUCTURE,
  [AltClass.MULTI]: AltClassTruncated.MULTI,
  [AltClass.NATURAL_RESOURCES]: AltClassTruncated.NATURAL_RESOURCES,
  [AltClass.PRIVATE_DEBT]: AltClassTruncated.PRIVATE_DEBT,
  [AltClass.PRIVATE_EQUITY]: AltClassTruncated.PRIVATE_EQUITY,
  [AltClass.REAL_ESTATE]: AltClassTruncated.REAL_ESTATE,
  [AltClass.VENTURE_CAPITAL]: AltClassTruncated.VENTURE_CAPITAL,
  // French options
  [AltClass.CAPITAL_INVESTISSEMENT]: AltClassTruncated.CAPITAL_INVESTISSEMENT,
  [AltClass.CAPITAL_RISQUE]: AltClassTruncated.CAPITAL_RISQUE,
  [AltClass.DETTE_PRIVEE]: AltClassTruncated.DETTE_PRIVEE,
  [AltClass.FONDS_SPECULATIFS]: AltClassTruncated.FONDS_SPECULATIFS,
  [AltClass.IMMOBILIER]: AltClassTruncated.IMMOBILIER,
  [AltClass.RESSOURCES_NATURELLES]: AltClassTruncated.RESSOURCES_NATURELLES,
} as const;

export type AltDates = {
  finalClose?: Date;
  inception?: Date;
  interimClose?: Date;
  latestClose?: Date;
  growth?: Date;
};

export enum AltQuartile {
  FIRST = '1st',
  SECOND = '2nd',
  THIRD = '3rd',
  FOURTH = '4th',
}

// The Alternative Investment instrument model uses this type
type Alt = Instrument &
  ShelvableInstrument & {
    assetStatus?: InstrumentAssetStatus;
    // Financial properties
    called?: number;
    carriedInterest?: {
      basis?: string;
      rate?: number;
    };
    chargeFrequency?: string;
    commitment?: {
      size: number;
      currency: string;
      gpCommitmentPercent: number;
      gpCommitmentValue?: number;
    };
    distributed?: number;
    gpCatchUpRate?: number;
    gpCommitments?: number;
    hurdleRate?: number;
    remaining?: number;
    fundFormationCost?: number;

    // Performance properties
    medianBenchmarkNetIrr?: number;
    netIrr?: number;
    performance: AltPerformance;
    performanceBase?: AltPerformance;
    quartile?: string;

    // Metadata
    codes: Array<string>;
    dataProviders?: Array<DATA_PROVIDER_NAME>;
    dates: AltDates;
    description?: string;
    disclaimers?: InstrumentDisclaimer[];
    domicile?: string;
    externalId?: string;
    managementFee?: {
      investPeriod?: number;
      postInvestPeriod?: number;
      reductionMechanism?: string;
    };
    manufacturer?: string;
    objective?: LocalizedString;
    status?: string;
    vintage?: number;
    hasPerformanceFromBenchmark?: boolean;
  };

export default Alt;
